import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { testResultsApi, studentsApi, testsApi } from '../services/api';
import { TestResult, Student, Test } from '../types';
import { ImportResultsModal } from '../components/ImportResultsModal';

export const Results = () => {
  const [results, setResults] = useState<TestResult[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [tests, setTests] = useState<Test[]>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingResult, setEditingResult] = useState<TestResult | null>(null);
  const [selectedTestId, setSelectedTestId] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [formData, setFormData] = useState({
    student_id: '',
    test_id: '',
    score: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resultsResponse, studentsResponse, testsResponse, classesResponse] = await Promise.all([
        testResultsApi.getAll({ class: selectedClass || undefined }),
        studentsApi.getAll(),
        testsApi.getAll(),
        studentsApi.getClasses()
      ]);
      
      setResults(resultsResponse.data);
      setStudents(studentsResponse.data);
      setTests(testsResponse.data);
      setClasses(classesResponse.data);
    } catch (_err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass]);

  const handleAddResult = () => {
    setEditingResult(null);
    setFormData({
      student_id: '',
      test_id: '',
      score: ''
    });
    setShowModal(true);
  };

  const handleEditResult = (result: TestResult) => {
    setEditingResult(result);
    setFormData({
      student_id: result.student_id.toString(),
      test_id: result.test_id.toString(),
      score: result.score.toString()
    });
    setShowModal(true);
  };

  const handleDeleteResult = async (id: number) => {
    if (!confirm('Are you sure you want to delete this test result?')) return;
    
    try {
      await testResultsApi.delete(id);
      setResults(results.filter(r => r.id !== id));
    } catch (_err) {
      setError('Failed to delete result');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const resultData = {
      student_id: parseInt(formData.student_id),
      test_id: parseInt(formData.test_id),
      score: parseFloat(formData.score)
    };

    try {
      if (editingResult) {
        const response = await testResultsApi.update(editingResult.id, resultData);
        setResults(results.map(r => 
          r.id === editingResult.id ? { ...response.data, 
            student_name: students.find(s => s.id === response.data.student_id)?.name,
            test_name: tests.find(t => t.id === response.data.test_id)?.name,
            test_category: tests.find(t => t.id === response.data.test_id)?.category,
            test_date: tests.find(t => t.id === response.data.test_id)?.date
          } : r
        ));
      } else {
        const response = await testResultsApi.create(resultData);
        const newResult = {
          ...response.data,
          student_name: students.find(s => s.id === response.data.student_id)?.name,
          test_name: tests.find(t => t.id === response.data.test_id)?.name,
          test_category: tests.find(t => t.id === response.data.test_id)?.category,
          test_date: tests.find(t => t.id === response.data.test_id)?.date
        };
        setResults([...results, newResult]);
      }
      
      setShowModal(false);
      setError('');
    } catch (_err) {
      setError('Failed to save result');
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 9) return 'text-green-600 bg-green-50';
    if (score >= 8) return 'text-blue-600 bg-blue-50';
    if (score >= 7) return 'text-yellow-600 bg-yellow-50';
    if (score >= 6) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Grammar & Vocabulary': 'bg-blue-100 text-blue-800',
      'Literature Analysis': 'bg-green-100 text-green-800',
      'Test Preparation': 'bg-yellow-100 text-yellow-800',
      'Writing Skills': 'bg-purple-100 text-purple-800',
      'Reading Comprehension': 'bg-red-100 text-red-800',
      'Speaking & Listening': 'bg-indigo-100 text-indigo-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort results
  const filteredResults = useMemo(() => {
    let filtered = results;
    
    // Apply test filter
    if (selectedTestId) {
      filtered = filtered.filter(result => result.test_id === parseInt(selectedTestId));
    }
    
    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: string | number = 0;
        let bValue: string | number = 0;
        
        switch (sortField) {
          case 'student_name':
            aValue = a.student_name || students.find(s => s.id === a.student_id)?.name || '';
            bValue = b.student_name || students.find(s => s.id === b.student_id)?.name || '';
            break;
          case 'score':
            aValue = a.score;
            bValue = b.score;
            break;
          default:
            return 0;
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return sortDirection === 'asc' ? comparison : -comparison;
        } else {
          const comparison = (aValue as number) - (bValue as number);
          return sortDirection === 'asc' ? comparison : -comparison;
        }
      });
    }
    
    return filtered;
  }, [results, selectedTestId, sortField, sortDirection, students]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading results...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Test Results</h1>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium"
            >
              Import Results
            </button>
            <button
              onClick={handleAddResult}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium"
            >
              Add Result
            </button>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Filter by Test:</label>
              <select
                value={selectedTestId}
                onChange={(e) => setSelectedTestId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
              >
                <option value="">All Tests</option>
                {tests.map((test) => (
                  <option key={test.id} value={test.id}>
                    {test.name} - {test.category} ({new Date(test.date).toLocaleDateString()})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Filter by Class:</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px]"
              >
                <option value="">All Classes</option>
                {classes.map((className) => (
                  <option key={className} value={className}>
                    {className}
                  </option>
                ))}
              </select>
            </div>

            {(selectedTestId || selectedClass) && (
              <button
                onClick={() => {
                  setSelectedTestId('');
                  setSelectedClass('');
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear All Filters
              </button>
            )}

            <div className="ml-auto text-sm text-gray-600">
              Showing {filteredResults.length} of {results.length} results
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test Date
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('student_name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Student</span>
                      {sortField === 'student_name' && (
                        <span className="text-blue-600">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Class
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('score')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Score</span>
                      {sortField === 'score' && (
                        <span className="text-blue-600">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Class Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Overall Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResults.map((result) => (
                  <tr key={result.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(result.test_date || tests.find(t => t.id === result.test_id)?.date || '').toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {result.student_name || students.find(s => s.id === result.student_id)?.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {result.student_class || students.find(s => s.id === result.student_id)?.class || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {result.test_name || tests.find(t => t.id === result.test_id)?.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        getCategoryColor(result.test_category || tests.find(t => t.id === result.test_id)?.category || '')
                      }`}>
                        {result.test_category || tests.find(t => t.id === result.test_id)?.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getScoreColor(result.score)}`}>
                        {result.score}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        #{result.class_rank || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        #{result.overall_rank || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditResult(result)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteResult(result.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredResults.length === 0 && results.length > 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              No test results found for the selected filter.
            </p>
          </div>
        )}

        {results.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">No test results found.</p>
          </div>
        )}

        {showImportModal && (
          <ImportResultsModal
            onClose={() => setShowImportModal(false)}
            onImported={() => { fetchData(); }}
          />
        )}

        {/* Modal for Add/Edit Result */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingResult ? 'Edit Test Result' : 'Add New Test Result'}
                </h3>
                
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student *
                    </label>
                    <select
                      required
                      value={formData.student_id}
                      onChange={(e) => setFormData({...formData, student_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a student</option>
                      {students.map((student) => (
                        <option key={student.id} value={student.id}>
                          {student.name} - {student.class}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test *
                    </label>
                    <select
                      required
                      value={formData.test_id}
                      onChange={(e) => setFormData({...formData, test_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a test</option>
                      {tests.map((test) => (
                        <option key={test.id} value={test.id}>
                          {test.name} - {test.category} ({new Date(test.date).toLocaleDateString()})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Score (%) *
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      max="10"
                      step="0.1"
                      value={formData.score}
                      onChange={(e) => setFormData({...formData, score: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter score (0-10)"
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      {editingResult ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};